/* eslint-disable react/jsx-no-target-blank */
import {Box} from '@mui/material';
import {OfficeContextType} from '../../contexts/office/types';
import {OfficeContext} from '../../contexts/office/context';
import {BackendApiManager} from '../../managers/backend-api';
import {AppContextType} from '../../contexts/app/types';
import {AppContext} from '../../contexts/app/context';
import {ThemeMode} from '../../enums/theme-mode';
import {LoadingContentAbsolute} from '../loading-content/loading-content-absolute';
import {useEffect, useContext, useState} from 'react';
import 'rapidoc';

/**
 * API documentation
 */
export default function ApiDocs() {
  // Get context
  const appContext: AppContextType = useContext(AppContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [isLoading, setIsLoading] = useState(true);

  // Handle Theme
  const applyTheme = () => {
    // Map doc
    const doc: any = document.getElementById('thedoc');

    // Apply it
    doc.setAttribute(
      'theme',
      appContext.state.currentThemeMode === ThemeMode.Light ? 'light' : 'dark'
    );

    // Apply style to Navigation bar
    if (appContext.state.currentThemeMode === ThemeMode.Light) {
      doc.setAttribute('nav-bg-color', '#FFFFFF');
    } else {
      doc.setAttribute('nav-bg-color', '#333');
    }
  };

  // On Mount
  useEffect(() => {
    // Apply theme
    applyTheme();

    // Get Spec
    BackendApiManager.instance.get('/open-api').then((response) => {
      // Ensure that the DOM is loaded, then add the event listener. Map doc
      const doc: any = document.getElementById('thedoc');

      // Load Spec
      doc.loadSpec(response.data);

      // Log
      console.debug('Spec Loaded');

      // Set Loading
      setIsLoading(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Property 'rapi-doc' does not exist on type 'JSX.IntrinsicElements'
  // https://stackoverflow.com/a/33471928/13157395
  const RapiDoc: any = `rapi-doc`;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: !isLoading ? 68 : 0,
        left: !isLoading && officeContext.state.drawerOpened ? '240px' : 0,
        bottom: 0,
        right: 0,
      }}
    >
      {isLoading ? <LoadingContentAbsolute /> : undefined}
      <RapiDoc
        id="thedoc"
        _1="General"
        update-route="true"
        route-prefix="#"
        sort-tags="true"
        sort-endpoints-by="path"
        goto-path=""
        fill-request-fields-with-example="true"
        persist-auth="true"
        _2="UI Colors and Fonts"
        header-color="#00a3ff"
        primary-color="#00a3ff"
        load-fonts="false"
        regular-font="'Roboto', sans-serif"
        font-size="default"
        _3="Navigation bar settings"
        use-path-in-nav-bar="false"
        nav-item-spacing="relaxed"
        _4="UI Layout and Placement"
        layout="row"
        render-style="focused"
        on-nav-tag-click="show-description"
        default-schema-tab="schema"
        _5="Hide/Show Sections"
        show-components="true"
        show-header="false"
        allow-spec-url-load="false"
        allow-spec-file-load="false"
        allow-spec-file-download="false"
        allow-server-selection="false"
        allow-advanced-search="false"
      >
        <div slot="overview" className="x-overview">
          <br />

          <section>
            <h2>The meaning of Sioslife</h2>
            <p>
              Sioslife is a company that develops innovative technology solutions that help overcome
              the challenges of the best age through user-friendly interactive platforms.
            </p>
            <p>
              The elderly population and technology were two distant realities that could be brought
              together. When noticing this problem that exponentiated senior loneliness and lack of
              social activity, we realized that everyday challenges could be solved through
              adaptable and innovative solutions.
            </p>
            <p>
              Our interactive systems suit each user. A clear system that gives freedom to the
              senior and a peace of mind that only this can give. An accessible and present system,
              just like the Sioslife team. A system that eliminates borders, and that enhances the
              re-creation of a community united by technology. All in one!
            </p>
          </section>

          <br />

          <section>
            <h2>What is the Sioslife Integration Layer?</h2>
            <p>
              Because we believe in an inclusive world for the elderly, Sioslife creates
              technological solutions that contribute to a more active and happier aging.
            </p>
            <p>
              Sioslife Integration Layer is a REST API system that powers our platform for
              interactions between seniors and caregivers. In this API is a software layer
              connecting and optimizing communications between our own applications, external
              services and various partners.
            </p>
          </section>

          <br />

          <section>
            <h2>What is a REST API, anyway?</h2>
            <p>
              API is short for '
              <a href="https://en.wikipedia.org/wiki/API" target="_blank">
                Application Programming Interfaces
              </a>
              '. An API is a set of rules that lets programs talk to each other, exposing data and
              functionality across the Internet in a consistent format.
            </p>
            <p>
              REST stands for '
              <a
                href="http://www.ics.uci.edu/~fielding/pubs/dissertation/rest_arch_style.htm"
                target="_blank"
              >
                Representational State Transfer
              </a>
              '. This is an architectural pattern that describes how distributed systems can expose
              a consistent interface. When people use the term 'REST API', they are generally
              referring to an API accessed using the HTTP protocol at a predefined set of URLs.
            </p>
            <p>
              These URLs represent various resources — any information or content accessed at that
              location, which can be returned as JSON, HTML, audio files, or images. Often resources
              have one or more methods that can be performed on them over HTTP, like{' '}
              <code>GET</code>,<code>POST</code>, <code>PUT</code>, <code>PATCH</code> and{' '}
              <code>DELETE</code>. How they are defined is confusing, but the general rule is:
            </p>
            <ul>
              <li>
                <code>GET</code> to obtain a list of resources or a single resource
              </li>
              <li>
                <code>POST</code> to create resources
              </li>
              <li>
                <code>PUT</code> to update all properties of a single resource
              </li>
              <li>
                <code>PATCH</code> to update some properties of a list of resources or a single
                resource
              </li>
              <li>
                <code>DELETE</code> to delete a list of resources or a single resource
              </li>
            </ul>
          </section>
        </div>

        <div slot="auth" className="x-auth">
          <p>
            Authentication is when an entity proves an identity. In other words, Authentication
            proves that you are who you say you are. This is like having a driver license which is
            given by a trusted authority that the requester, such as a police officer, can use as
            evidence that suggests you are in fact who you say you are.
          </p>
          <p>
            While there are as many proprietary authentication methods as there are systems which
            utilize them, they are largely variations of a few major approaches (you can read more
            about it &#32;
            <a
              target="_blank"
              href="https://blog.restcase.com/4-most-used-rest-api-authentication-methods/"
            >
              here
            </a>
            ). Sioslife Integration Layer uses 2 of them:
          </p>
          <ul>
            <li>Bearer Authentication</li>
            <li>API Keys</li>
          </ul>

          <h3>Bearer Authentication</h3>
          <p>
            Bearer authentication (also called token authentication) is an HTTP authentication
            scheme that involves security tokens called bearer tokens.
          </p>
          <p>
            The name "Bearer authentication" can be understood as "give access to the bearer of this
            token". The bearer token allowing access to a certain resource or URL and most likely is
            a cryptic string, usually generated by the server in response to a login request.
          </p>
          <p>
            For the Sioslife Integration Layer, the client must send this token in the
            "Authorization" header when making requests to protected resources: <br />
            <code>Authorization: Bearer &lt;token&gt;</code>
          </p>
          <p>
            The Bearer authentication scheme was originally created as part of OAuth 2.0 in RFC-6750
            but is sometimes also used on its own.
          </p>
          <p>
            Similarly to Basic authentication, Bearer authentication should only be used over HTTPS
            (SSL).
          </p>
          <p>
            <b>
              Sioslife Integration Layer uses Bearer authentication to authenticate Users and
              Devices using the system.
            </b>
          </p>

          <h3>API Key</h3>
          <p>
            An Application Programming Interface key (API key) is a unique identifier used to
            authenticate a user, developer, or calling program to an API. However, they are
            typically used to authenticate a project with the API rather than a human user.
            Different platforms may implement and use API keys in different ways.
          </p>
          <p>
            The API key often acts as both a unique identifier and a secret token for
            authentication, and will generally have a set of access rights on the API associated
            with it.
          </p>
          <p>
            For the Sioslife Integration Layer, the client must send this key in a "X-API-KEY"
            header when making requests to protected resources:
            <br />
            <code>X-API-KEY: &lt;key&gt;</code>
          </p>
          <p>
            <b>
              Sioslife Integration Layer uses API Key authentication to authenticate External
              services and Partners using the system.
            </b>
          </p>
        </div>
      </RapiDoc>
    </Box>
  );
}
